import * as z from 'zod';
import type { ZodOpenApiPathsObject } from 'zod-openapi';
import { problemResponse } from './common.js';

export const SettingSchema = z
  .object({
    key: z.string(),
    kind: z.enum(['integer', 'url', 'secret', 'text']),
    description: z.string(),
    value: z
      .union([z.number(), z.string()])
      .nullable()
      .meta({ description: 'Always null for secrets' }),
    isSet: z.boolean(),
    default: z.number().nullable(),
    min: z.number().nullable(),
    max: z.number().nullable(),
    updatedAt: z.iso.datetime().nullable(),
    updatedBy: z.uuid().nullable(),
  })
  .meta({ id: 'Setting' });

export const UpdateSettingSchema = z
  .object({ value: z.union([z.number(), z.string()]).nullable() })
  .meta({ id: 'UpdateSetting' });

export const N8nTestResultSchema = z
  .object({
    delivered: z.boolean(),
    status: z.number().int().nullable().meta({ description: 'HTTP status returned by n8n' }),
    error: z.string().nullable(),
  })
  .meta({ id: 'N8nTestResult' });

export type SettingDto = z.infer<typeof SettingSchema>;
export type UpdateSettingRequest = z.infer<typeof UpdateSettingSchema>;
export type N8nTestResult = z.infer<typeof N8nTestResultSchema>;

const json = <T extends z.ZodType>(schema: T) => ({ 'application/json': { schema } });

export const settingsPaths: ZodOpenApiPathsObject = {
  '/api/v1/settings': {
    get: {
      summary: 'All settings with current values (secrets masked)',
      responses: {
        '200': { description: 'OK', content: json(z.array(SettingSchema)) },
        '401': problemResponse('Not signed in'),
        '403': problemResponse('Missing permission'),
      },
    },
  },
  '/api/v1/settings/{key}': {
    put: {
      summary: 'Change one setting (null clears URL / secret settings)',
      requestParams: { path: z.object({ key: z.string() }) },
      requestBody: { required: true, content: json(UpdateSettingSchema) },
      responses: {
        '200': { description: 'Saved', content: json(SettingSchema) },
        '400': problemResponse('Invalid value'),
        '401': problemResponse('Not signed in'),
        '403': problemResponse('Missing permission'),
        '404': problemResponse('Unknown setting'),
      },
    },
  },
};
